import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Form, FloatingLabel } from "react-bootstrap";
import { 
  FaUsers, FaHome, FaUserInjured, FaUserNurse, FaCalendarAlt, 
  FaCheckCircle, FaEye, FaGlasses, FaHandHoldingMedical, 
  FaStethoscope, FaSyringe, FaSearch, FaExclamationTriangle 
} from "react-icons/fa";
import AppNav from "../header/AppNav";
import { DashboardService } from "../services/DashboardService";
import { AshaAbstractReportService } from "../services/AshaAbstractReportService";
import '../style/css/style.css'
import { TalukServices } from "../services/Taluk";
import { PHCServices } from "../services/Phcs";
import { AshaBlockServices } from "../services/AshaBlocks";

function Dashboard() {
  const [data, setData] = useState(0);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem("user"));
  const role = user?.roleDetails?.role;
  const [abstractTotals, setAbstractTotals] = useState({
    totalHouses: 0,
    housesScreened: 0,
    totalPopulation: 0,
    personsScreened: 0,
    personsAbove40Screened: 0,
    personsWithDiabetesScreened: 0,
    personsReferredToSecondaryCamp: 0,
  });

  // Dependent filters state
  const [taluks, setTaluks] = useState([]);
  const [phcs, setPhcs] = useState([]);
  const [ashaBlocks, setAshaBlocks] = useState([]);

  const [selectedTaluk, setSelectedTaluk] = useState("");
  const [selectedPhc, setSelectedPhc] = useState("");
  const [selectedAshaBlock, setSelectedAshaBlock] = useState("");
  
  useEffect(() => {
    const fetchCount = async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      const resposne = await DashboardService.fetchDashboardData(user);
      setData(resposne.data);
      setLoading(false);
    };

    fetchCount();
  }, []);

  // Helper to fetch and compute abstract totals based on current role and optional filter selections
  const fetchAndSetAbstractTotals = async (talukIdArg, phcIdArg, ashaBlockIdArg) => {
    try {
      let res;
      if (role === "Super Admins") {
        if (ashaBlockIdArg) {
          res = await AshaAbstractReportService.getByAshaBlockId(ashaBlockIdArg);
        } else if (phcIdArg) {
          res = await AshaAbstractReportService.getByPhcId(phcIdArg);
        } else if (talukIdArg) {
          res = await AshaAbstractReportService.getByTalukId(talukIdArg);
        } else {
          res = await AshaAbstractReportService.getSuperAdmin();
        }
      } else if (role === "Taluk") {
        if (ashaBlockIdArg) {
          res = await AshaAbstractReportService.getByAshaBlockId(ashaBlockIdArg);
        } else if (phcIdArg) {
          res = await AshaAbstractReportService.getByPhcId(phcIdArg);
        } else {
          const talukId = talukIdArg || user?.talukDetails?.id;
          if (talukId) res = await AshaAbstractReportService.getByTalukId(talukId);
        }
      } else if (role === "PHC") {
        if (ashaBlockIdArg) {
          res = await AshaAbstractReportService.getByAshaBlockId(ashaBlockIdArg);
        } else {
          const phcId = phcIdArg || user?.phcDetails?.id;
          if (phcId) res = await AshaAbstractReportService.getByPhcId(phcId);
        }
      } else if (role === "Asha Block") {
        const abId = ashaBlockIdArg || user?.ashaBlockDetails?.id;
        if (abId) res = await AshaAbstractReportService.getByAshaBlockId(abId);
      }

      let list = [];
      if (Array.isArray(res)) {
        list = res;
      } else if (res && Array.isArray(res.data)) {
        list = res.data;
      } else if (res && res.data && Array.isArray(res.data.data)) {
        list = res.data.data;
      }

      const totals = (list || []).reduce((acc, r) => {
        acc.totalHouses += r?.totalHouses || 0;
        acc.housesScreened += r?.housesScreened || 0;
        acc.totalPopulation += r?.totalPopulation || 0;
        acc.personsScreened += r?.personsScreened || 0;
        acc.personsAbove40Screened += r?.personsAbove40Screened || 0;
        acc.personsWithDiabetesScreened += r?.personsWithDiabetesScreened || 0;
        acc.personsReferredToSecondaryCamp += r?.personsReferredToSecondaryCamp || 0;
        return acc;
      }, {
        totalHouses: 0,
        housesScreened: 0,
        totalPopulation: 0,
        personsScreened: 0,
        personsAbove40Screened: 0,
        personsWithDiabetesScreened: 0,
        personsReferredToSecondaryCamp: 0,
      });

      setAbstractTotals(totals);
    } catch (e) {
      // silent fail
    }
  };

  // Fetch abstract report data based on role on page load
  useEffect(() => {
    fetchAndSetAbstractTotals(selectedTaluk, selectedPhc, selectedAshaBlock);
  }, []);

  // Helpers for dependent dropdowns
  const getPhcs = async (talukId) => {
    try {
      if (!talukId) {
        setPhcs([]);
        return;
      }
      const res = await PHCServices.gePhcsByTalukId(talukId);
      if (Array.isArray(res)) {
        setPhcs(res);
      } else if (res && typeof res === 'object') {
        const values = Object.values(res);
        setPhcs(values.length > 0 && values[0]?.id ? values : []);
      } else {
        setPhcs([]);
      }
    } catch (error) {
      setPhcs([]);
    }
  };

  const loadAshaBlocksForPhc = async (phcId) => {
    try {
      const blocksRes = await AshaBlockServices.geAshaBlockByPhcId(phcId);
      setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
    } catch (error) {
      setAshaBlocks([]);
    }
  };

  const handleTalukChange = async (e) => {
    const value = e.target.value;
    setSelectedTaluk(value);
    setSelectedPhc('');
    setSelectedAshaBlock('');
    if (value) {
      await getPhcs(value);
      setAshaBlocks([]);
    } else {
      setPhcs([]);
      setAshaBlocks([]);
    }
    // Update primary screening data based on selected Taluk
    await fetchAndSetAbstractTotals(value, '', '');
  };

  const handlePhcChange = async (e) => {
    const value = e.target.value;
    setSelectedPhc(value);
    setSelectedAshaBlock('');
    if (value) {
      await loadAshaBlocksForPhc(value);
    } else {
      setAshaBlocks([]);
    }
    // Update primary screening data based on selected PHC
    await fetchAndSetAbstractTotals(selectedTaluk, value, '');
  };

  const handleAshaBlockChange = (e) => {
    const value = e.target.value;
    setSelectedAshaBlock(value);
    // Update primary screening data based on selected Asha Block
    fetchAndSetAbstractTotals(selectedTaluk, selectedPhc, value);
  };

  // Initialize dropdown data based on role
  useEffect(() => {
    const initFilters = async () => {
      try {
        if (role === "Super Admins") {
          const taluksRes = await TalukServices.getAllTaluks();
          setTaluks(Array.isArray(taluksRes) ? taluksRes : Object.values(taluksRes || {}));
        } else if (role === "Taluk") {
          const talukId = user?.talukDetails?.id;
          if (talukId) {
            setSelectedTaluk(String(talukId));
            const phcRes = await PHCServices.gePhcsByTalukId(talukId);
            setPhcs(Array.isArray(phcRes) ? phcRes : Object.values(phcRes || {}));
            const blocksRes = await AshaBlockServices.getAshaBlockByTalukId(talukId);
            setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
          }
        } else if (role === "PHC") {
          const phcId = user?.phcDetails?.id;
          if (phcId) {
            setSelectedPhc(String(phcId));
            const blocksRes = await AshaBlockServices.geAshaBlockByPhcId(phcId);
            setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
          }
        }
      } catch (e) {
        // Silent fail for dashboard filters
      }
    };
    initFilters();
  }, []);

  return (
    <div className="dashboard-container">
      <AppNav />
      <Container className="dashborad-top-spacing">
        <Row className="g-4">
          <Col md={{ span: 3, offset: 4 }}>
          <Card className="shadow border-0 rounded-3 p-0 overflow-hidden dashboard-card">
  <div className="icon-section d-flex align-items-center justify-content-center icon-box">
    <FaUserNurse size={20}/>
  </div>

  <div className="d-flex text-center">
    <div className="flex-fill border-end p-3">
      <h4 className="fw-bold mb-0">{loading ? "..." :  data.totalAshaCount || 0}</h4>
      <small className="text-muted">Total Ashas count</small>
    </div>
    <div className="flex-fill p-3">
      <h4 className="fw-bold mb-0">{loading ? "..." : data.totalAshasAdded || 0}</h4>
      <small className="text-muted">Total Ashas added</small>
    </div>
  </div>
</Card>

          </Col>
        </Row>
      </Container>
<Row className="g-4">
  <Col md={{ span: 6, offset: 1 }}>
  <h3 className="fw-bold mb-0" style={{ color: "#003366" }}>Primary Screening Data</h3>
  </Col>
</Row>
      {/* Role-based dependent filters */}
      <Container className="dashborad-top-spacing">
        <Row className="mb-3">
          {role === "Super Admins" && (
            <>
              <Col md={4}>
                <FloatingLabel controlId="taluk" label="Taluk" className="mt-3">
                  <Form.Select
                    value={selectedTaluk}
                    onChange={handleTalukChange}
                    style={{ height: '58px' }}
                    className="align-items-center d-flex"
                  >
                    <option value="">All Taluks</option>
                    {taluks.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.taluk}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col md={4}>
                <FloatingLabel controlId="phc" label="PHC" className="mt-3">
                  <Form.Select
                    value={selectedPhc}
                    onChange={handlePhcChange}
                    disabled={!selectedTaluk}
                    style={{ height: '58px' }}
                    className="align-items-center d-flex"
                  >
                    <option value="">All PHCs</option>
                    {phcs.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.phc}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Col>
            </>
          )}

          {role === "Taluk" && (
            <Col md={4}>
              <FloatingLabel controlId="phc" label="PHC" className="mt-3">
                <Form.Select
                  value={selectedPhc}
                  onChange={handlePhcChange}
                  style={{ height: '58px' }}
                  className="align-items-center d-flex"
                >
                  <option value="">All PHCs</option>
                  {phcs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.phc}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
          )}

          {(role === "Super Admins" || role === "Taluk" || role === "PHC") && (
            <Col md={4}>
              <FloatingLabel controlId="ashaBlock" label="Asha Block" className="mt-3">
                <Form.Select
                  value={selectedAshaBlock}
                  onChange={handleAshaBlockChange}
                  disabled={role === "Super Admins" ? !selectedPhc : (role === "Taluk" ? !selectedPhc : false)}
                  style={{ height: '58px' }}
                  className="align-items-center d-flex"
                >
                  <option value="">All Asha Blocks</option>
                  {ashaBlocks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.ashaBlock}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
          )}
        </Row>
      </Container>
      <Container className="dashborad-top-spacing">
        <Row className="g-4">
          <Col md={3}>
            <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
              <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                <div className="icon-section d-flex align-items-center justify-content-center icon-box">
                  <FaHome size={24} />
                </div>
                <div className="text-end">
                  <h2 className="new-card-number mb-0">{abstractTotals.totalHouses}</h2>
                </div>
              </div>
              <div className="new-card-footer px-4 pb-4">
                <p className="new-card-label mb-0">Total Houses</p>
              </div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
              <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                <div className="icon-section d-flex align-items-center justify-content-center icon-box">
                  <FaHome size={24}/>
                </div>
                <div className="text-end">
                  <h2 className="new-card-number mb-0">{abstractTotals.housesScreened}</h2>
                </div>
              </div>
              <div className="new-card-footer px-4 pb-4">
                <p className="new-card-label mb-0">Houses Screened</p>
              </div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
              <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                <div className="icon-section d-flex align-items-center justify-content-center icon-box">
                  <FaUsers size={24}/>
                </div>
                <div className="text-end">
                  <h2 className="new-card-number mb-0">{abstractTotals.totalPopulation}</h2>
                </div>
              </div>
              <div className="new-card-footer px-4 pb-4">
                <p className="new-card-label mb-0">Total Population</p>
              </div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
              <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                <div className="icon-section d-flex align-items-center justify-content-center icon-box">
                  <FaEye size={24}/>
                </div>
                <div className="text-end">
                  <h2 className="new-card-number mb-0">{abstractTotals.personsScreened}</h2>
                </div>
              </div>
              <div className="new-card-footer px-4 pb-4">
                <p className="new-card-label mb-0">Persons Screened</p>
              </div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
              <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                <div className="icon-section d-flex align-items-center justify-content-center icon-box">
                  <FaEye size={24}/>
                </div>
                <div className="text-end">
                  <h2 className="new-card-number mb-0">{abstractTotals.personsAbove40Screened}</h2>
                </div>
              </div>
              <div className="new-card-footer px-4 pb-4">
                <p className="new-card-label mb-0">Persons Above 40 Screened</p>
              </div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
              <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                <div className="icon-section d-flex align-items-center justify-content-center icon-box">
                  <FaStethoscope size={24}/>
                </div>
                <div className="text-end">
                  <h2 className="new-card-number mb-0">{abstractTotals.personsWithDiabetesScreened}</h2>
                </div>
              </div>
              <div className="new-card-footer px-4 pb-4">
                <p className="new-card-label mb-0">Diabetes Screened</p>
              </div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
              <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                <div className="icon-section d-flex align-items-center justify-content-center icon-box">
                  <FaSyringe size={24}/>
                </div>
                <div className="text-end">
                  <h2 className="new-card-number mb-0">{abstractTotals.personsReferredToSecondaryCamp}</h2>
                </div>
              </div>
              <div className="new-card-footer px-4 pb-4">
                <p className="new-card-label mb-0">Referred To Secondary Camp</p>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>

      {role === "Super Admins" && (
        <>
          <Row className="g-4">
            <Col md={{ span: 6, offset: 1 }}>
              <h3 className="fw-bold mb-0" style={{ color: "#003366" }}>Secondary Screening Data</h3>
            </Col>
          </Row>
          <Container className="dashborad-top-spacing">
            <Row className="g-4">
              <Col md={12}>
                <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
                  <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                    <h4 className="mb-0" style={{ color: '#003366' }}>Cataract</h4>
                  </div>
                  <div className="px-4 pb-4">
                    <Row className="g-3">
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Total Count</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>{loading ? "..." : (data.cataractPatientCount || 200)}</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Surgery Completed</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>234</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Follow-ups</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>145</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">No Action Taken</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>56</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Follow-up w/o Surgery</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>18</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Completed</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>234</div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Card>
              </Col>
              <Col md={12}>
                <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
                  <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                    <h4 className="mb-0" style={{ color: '#003366' }}>Glaucoma</h4>
                  </div>
                  <div className="px-4 pb-4">
                    <Row className="g-3">
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Total Count</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>{loading ? "..." : (data.glaucomaPatientCount || 150)}</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Surgery Completed</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>45</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Follow-ups</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>67</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">No Action Taken</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>8</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Follow-up w/o Surgery</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>3</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Completed</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>45</div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Card>
              </Col>
              <Col md={12}>
                <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
                  <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                    <h4 className="mb-0" style={{ color: '#003366' }}>Diabetic Retinopathy</h4>
                  </div>
                  <div className="px-4 pb-4">
                    <Row className="g-3">
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Total Count</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>{loading ? "..." : (data.diabeticRetinopathyCount || 80)}</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Surgery Completed</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>12</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Follow-ups</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>45</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">No Action Taken</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>28</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Follow-up w/o Surgery</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>4</div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Completed</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>12</div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Card>
              </Col>
              <Col md={12}>
                <Card className="new-dashboard-card shadow-sm border-0 rounded-4 p-0 overflow-hidden">
                  <div className="new-card-header d-flex align-items-center justify-content-between p-4">
                    <h4 className="mb-0" style={{ color: '#003366' }}>Other Conditions</h4>
                  </div>
                  <div className="px-4 pb-4">
                    <Row className="g-3">
                      <Col md={12}>
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted">Total Count</small>
                          <div className="fw-bold" style={{ color: '#003366' }}>{loading ? "..." : (data.otherIssuesCount || 20)}</div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Card>
              </Col>
            </Row>
          </Container>
        </>
      )}
    </div>
  );
}

export default Dashboard;
